/* eslint-disable max-len */
import React, { useState, useRef, useContext, useEffect } from 'react';
import _ from 'lodash';
import { ModalNext, ModalHeader, ModalBody, ModalFooter, InlineNotification } from '@jutro/components';
import { ViewModelForm, useDataRefresh } from '@xengage/gw-portals-viewmodel-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
// eslint-disable-next-line import/no-unresolved
import classNames from 'classnames';
import appConfig from 'app-config';
import messages from './FormModalPopover.messages';
import styles from './FormModalPopover.module.scss'; // for QnB
import peStyles from './PEFormModalPopover.module.scss'; // for ProducerEngage
import { Button } from '@jutro/legacy/components';

import { ScrollToError } from '@jutro/legacy/wizard-next';

function FormModalPopover(props) {
    const {
        showHeader,
        title,
        subtitle,
        // To display a simple message, for example, MLModalService.showErrorModal()
        simpleMessage,
        // To display a component as content, for example, adding MLBankAccountDetails in PaymentDetails page
        CustomComponent,
        // ViewModelForm
        uiProps,
        viewModel,
        actionBtnLabel = messages.messageActionLabel,
        cancelBtnLabel,
        isOpen = false,
        onResolve = _.noop,
        onReject = _.noop,
        authHeader,
        // static props, shared with CustomComponent and ViewModelForm
        overrideProps,
        // dynamic props
        getOverrideProps,
        // define styles for Model Body section
        modalBodyStyles,
        // callback function to perform additional actions like validation, etc.
        callbackMap,
        // before save validation. maybe used for save result, recommend to use onResolve
        onAsyncSaveAction,
        onSaveAction = _.noop,
        onCancelAction = _.noop,
        //
        onFieldValueChange,
        customComponentResolvers,
        disableButton,
        showXCloseButton = true,
        // show footer even if custom component is available.
        showFooterOnCustomComponent = false,
        messageNextToButton,
        buttonType,
        additionalPEStyle,
        viewModelService,
        additionalStyle,
        footerBtnRight = false,
        closeSimpleMessage = true,
        // for validation message as banner message, pass setBannerMessage to CustomComponent for reset
        showBannerMessage = false,
        showActionButton = true, // In case only Close button is shown. Default is true
        discardChangesOnCancel = false,
        closeOnEsc = false,
        closeOnOverlayClick = false,
        editValidateOnOpen = false,
        isFromMLResendEsign = false
    } = props;

    const isGPA = appConfig.portalCode === 'GPA';
    const isQnB = appConfig.portalCode === 'QB';

    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const [errorTimestamp, setErrorTimestamp] = useState(0);
    const { isComponentValid, onValidate } = useValidation('FormModalPopover');
    const { refreshData } = useDataRefresh();

    const [bannerMessage, setBannerMessage] = useState(null);
    const bannerMessageRef = useRef();

    const formProps = !_.isUndefined(getOverrideProps) ? getOverrideProps() : overrideProps;

    const callBackMaps = !_.isUndefined(callbackMap) ? callbackMap : {};

    const originalVM = useRef();
    useEffect(() => {
        originalVM.current = discardChangesOnCancel && viewModel ? viewModelService.clone(viewModel).value : null;
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (editValidateOnOpen) {
            setShowValidationErrors(true);
            setErrorTimestamp(errorTimestamp+1);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const onModelChange = () => {
        refreshData();
    };

    // ViewModelForm.onFieldValueChange() internally _.set(model, path, value) and calls onModelChange to refresh.
    // However, field level onValueChange() overrides this without calling onModelChange for refresh.
    // Thus use onFieldValueChange to update field value and refresh. See example at MLDisclosureComponent for EditDisclosurePopup
    const onValueChange = (value, path) => {
        if (onFieldValueChange) {
            onFieldValueChange(value, path);
        }
        refreshData();
    };

    const handleClose = () => {
        if (discardChangesOnCancel && viewModel) {
            _.set(viewModel, 'value', originalVM.current);
        }
        const isProductAvailable = sessionStorage.getItem('isProductAvailable');
        if (isQnB && isProductAvailable) {
            sessionStorage.removeItem('isProductAvailable');
        }
        onCancelAction();
        onReject();
    };

    const handleSave = (...args) => {
        //to Handle Invalid data from Esign Form added validation check
        if(isFromMLResendEsign === true){
            if (!isComponentValid || !viewModel.aspects.subtreeValid) {
                setShowValidationErrors(true);
                setErrorTimestamp(errorTimestamp+1);
                return;
            }
        }
        if (!isComponentValid) {
            setShowValidationErrors(true);
            setErrorTimestamp(errorTimestamp+1);
            return;
        }
        if (onAsyncSaveAction && onAsyncSaveAction !== _.noop) {
            if (!_.isUndefined(CustomComponent)) {
                onAsyncSaveAction(...args).then((value) => {
                    if (value) {
                        if (value !== true) {
                            if (showBannerMessage) {
                                setBannerMessage(value);
                            }
                        } else {
                            setBannerMessage(null);
                            onResolve(viewModel);
                        }
                    } else {
                        setBannerMessage(null);
                        refreshData();
                    }
                });
            } else {
                onAsyncSaveAction(viewModel).then((value) => {
                    if (value) {
                        onResolve(viewModel);
                    } else {
                        refreshData();
                    }
                });
            }
        } else {
            if (onSaveAction && onSaveAction !== _.noop) {
                if (!_.isUndefined(CustomComponent)) {
                    const result = onSaveAction(...args);
                    if (result !== true) {
                        if (showBannerMessage) {
                            setBannerMessage(result);
                        }
                        return;
                    }
                } else {
                    const result = onSaveAction(viewModel);
                    if (result !== true) {
                        if (showBannerMessage) {
                            setBannerMessage(result);
                        } else {
                            refreshData();
                            handleClose();
                        }
                        return;
                    }
                }
                setBannerMessage(null);
            }
            onResolve(viewModel);
        }
    };

    React.useEffect(() => {
        if (bannerMessage) bannerMessageRef.current.scrollIntoView({ behavior: 'smooth' });
    }, [bannerMessage]);

    const customComponentOverrides = {
        onValidate,
        showValidationErrors,
        setShowValidationErrors,
        viewModelService,
        viewModel,
        authHeader,
        setBannerMessage,
        ...(!_.has(formProps, 'onModelChange') && { onModelChange }),
        ...(!_.has(formProps, 'onSave') && { onSave: handleSave }),
        ...(!_.has(formProps, 'onCancel') && { onCancel: handleClose }),
        showErrorPopup: false,
        errorTimestamp,
        setErrorTimestamp,
        ...formProps
    };

    const showFooter = () => {
        return _.isUndefined(CustomComponent) || (showFooterOnCustomComponent && !!CustomComponent);
    };

    const resolvers = {
        resolveClassNameMap: modalBodyStyles,
        resolveCallbackMap: {
            onValidate: onValidate
        },
        resolveComponentMap: {
            ...customComponentResolvers
        }
    };

    const addValidation = () => {
        if (!_.isUndefined(formProps)) {
            Object.keys(formProps).forEach((key) => {
                const prop = formProps[key];
                if (prop && prop.isComponent) {
                    // Custom component must support these properties
                    // Custom component must be a child viewmodel of current viewmodel
                    // For example, AddDriverPopup includes NameDetails component.
                    // Name Details viewmodel personVM is a child of driverVM.
                    prop.onValidate = onValidate;
                    prop.showValidationErrors = showValidationErrors;
                    prop.onModelChange = onModelChange;
                }
            });
        }
        return formProps;
    };

    const checkDisableButton = () => {
        if (disableButton) {
            return disableButton(viewModel);
        }
        return false;
    };

    const overrides = { ...addValidation() };

    return (
        <ModalNext
            className={isGPA ? classNames(peStyles['ml-modal-dialog'], peStyles[additionalPEStyle]) : classNames(styles['ml-modal-dialog'], styles['ml-modal-dialog-qb'], styles[additionalStyle])}
            isOpen={isOpen}
            onRequestClose={handleClose}
            shouldCloseOnOverlayClick={closeOnOverlayClick}
            shouldCloseOnEsc={closeOnEsc}
        >
            <div ref={bannerMessageRef} />
            {showHeader &&
                <ModalHeader
                    id='modalHeader'
                    title={title}
                    onClose={showXCloseButton && handleClose}
                    ariaLabel="close"
                    contentLayout={{
                        component: 'div',
                        componentProps: {
                            className: isGPA ? peStyles['modal-header'] : styles['modal-header']
                        }
                    }}
                    titleLayout={{
                        component: 'grid',
                        componentProps: {
                            // eslint-disable-next-line no-nested-ternary
                            className: isGPA ? peStyles['modal-title'] : isQnB ? styles['modal-title-QnB'] : styles['modal-title'],
                            role: "dialog",
                            'aria-label': (title?.defaultMessage || title)?.toLowerCase(),
                            'aria-labelledby': isQnB ? (title?.defaultMessage || title)?.toLowerCase() : ''
                        }
                    }}
                    subtitle={subtitle}
                />
            }
            <ModalBody
                autoFocus={false}
                contentLayout={{
                    component: 'div',
                    componentProps: {
                        className: isGPA ? peStyles['modal-body'] : styles['modal-body-qb']
                    }
                }}
            >
                {showBannerMessage && !_.isEmpty(bannerMessage) && (
                    <div className='vertical-spacing-20'>
                        {/* We must use the error messageProp instead of the actual message property because the current version of Jutro requires a prefix */}
                        {/* or it will default to 'Error:'. We don't want this or the previous solution of using a white space as that breaks multiline alignment. */}
                        <InlineNotification
                            id='bannerMessage'
                            isDismissable={false}
                            messageProps={{
                                error: bannerMessage
                            }}
                            type='error'
                        />
                    </div>
                )}
                {!_.isEmpty(simpleMessage)
                    && (
                        <p>
                            {simpleMessage}
                        </p>
                    )}
                {!_.isUndefined(CustomComponent)
                    && (
                        <CustomComponent {...customComponentOverrides} />
                    )
                }
                {_.isEmpty(simpleMessage) && _.isUndefined(CustomComponent)
                    && (
                        <ViewModelForm
                            uiProps={uiProps}
                            model={viewModel}
                            onValueChange={onValueChange}
                            onModelChange={onModelChange}
                            overrideProps={overrides}
                            classNameMap={resolvers.resolveClassNameMap}
                            onValidationChange={onValidate}
                            showErrors={showValidationErrors}
                            componentMap={resolvers.resolveComponentMap}
                            callbackMap={callBackMaps}
                        />
                    )}
            </ModalBody>
            {showFooter()
                && (
                    <ModalFooter contentLayout={{
                        component: 'flex',
                        componentProps: {
                            className: footerBtnRight && isGPA ? classNames((styles['modal-footer'], peStyles['float-right'])) : styles['modal-footer']
                        }
                    }}
                    >

                        {(showActionButton && !_.isEmpty(simpleMessage) && closeSimpleMessage)
                            && (
                                <Button id="actionButton" className={isQnB ? "primaryButton": ''} onClick={handleClose} type="filled">
                                    {actionBtnLabel}
                                </Button>
                            )}
                        {(showActionButton && _.isEmpty(simpleMessage) || !closeSimpleMessage)
                            && (
                                <Button id="actionButton" onClick={handleSave}  className={isQnB ? "primaryButton": ''} type={buttonType || 'filled'} disabled={checkDisableButton()}>
                                    {actionBtnLabel}
                                </Button>
                            )}
                        {!_.isNil(messageNextToButton) && (
                            <span className={isGPA ? peStyles.messageNextToButton : styles.messageNextToButton}>{messageNextToButton}</span>
                        )}
                        {!_.isEmpty(cancelBtnLabel)
                            && (
                                <Button id="cancelButton" className={isQnB ? "widthSmall secondaryButton": ''} onClick={handleClose} type="outlined">
                                    {cancelBtnLabel}
                                </Button>
                            )
                        }
                    </ModalFooter>
                )}
            <ScrollToError counter={errorTimestamp} />
        </ModalNext>
    );
}

FormModalPopover.defaultProps = {
    showHeader: true
};

export default FormModalPopover;
